/**
 * «Прогнать на моделях» (спека §9.2 «Видео» п.1, §11; v2 §1.1–1.2, §1.6). Панель начинается со
 * СТРОКИ выбранного и кнопки «Выбрать модели» (№335: «не удобно что экран начинается с громоздкой
 * таблицы»); сама таблица — в окне: выбор (галочки по `default_on`: только Kling), уровень, чип лиц
 * «да / нет / ?» (сноска под таблицей), размер одной строкой «720p · 720×1280», цена; запасная модель
 * уровня (`fallback_for`) — серой вложенной строкой под родителем; у модели, отклоняющей лица, — «кадры с лицом → {запасная}». Среза «ниже подбородка» нет:
 * тело запуска без `crops`, кадры с лицом сервер маршрутизирует сам. Общие «бесшовно» и «референсы
 * платья»; блокеры со ссылками на кадры; разбор маршрутизированных и уже готовых клипов; итог с потолком
 * и балансом; кнопка запуска.
 *
 * ДЕНЬГИ: «Запустить N клипов за ≈ $X» — платно (оплачивает воркер). Кнопка живёт только на СВЕЖЕЙ
 * оценке текущего набора (`useEstimate.fresh`, 300 мс после последней правки) без блокеров; на время
 * запроса — замок, ключ идемпотентности — до успешного ответа (`useRunClips`). После успеха выбор
 * моделей снимается: повтор того же набора — осознанное действие, а не второй клик. Баланс ниже
 * оценки — жёлтое предупреждение, не блок (§11).
 */

import clsx from "clsx";
import { Play } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { Btn, ErrorLine, Hint, inputClass, Panel, selectClass } from "@/components/project/ui";
import { Dialog } from "@/components/trends/Dialog";
import { errorText, usd } from "@/lib/project";
import {
  useEstimate,
  useRunClips,
  useVideoOptions,
  useVideoSettings,
  type TimelineShot,
  type VideoModel,
} from "@/lib/video";
import {
  blockerShot,
  canRun,
  FACES_CHIP,
  FACES_NOTE,
  faceRoute,
  initialPick,
  modelBlock,
  modelRows,
  modelTotals,
  parseCap,
  pickedSummary,
  plural,
  routeLines,
  runBody,
  sizeChoices,
  sizeLabel,
  uniqueWarnings,
  type RunPick,
} from "@/lib/videoRun";

import { useModelLabels } from "./clips";

const FACES_TONE = {
  accepts: "bg-ok-bg text-ok",
  rejects: "bg-warn-bg text-warn",
  unknown: "bg-neutral-bg text-fg-muted",
} as const;

export function VideoRunPanel({
  pid,
  timeline,
  costCap,
}: {
  pid: number;
  timeline: TimelineShot[];
  costCap: number | null;
}) {
  const options = useVideoOptions();
  const label = useModelLabels();
  const models = useMemo(() => options.data?.models ?? [], [options.data]);
  const rows = useMemo(() => modelRows(models), [models]);
  const orderOf = (sid: number) => timeline.find((s) => s.shot_id === sid)?.order ?? null;
  const [touched, setTouched] = useState<Record<string, RunPick>>({});
  const [seamless, setSeamless] = useState(false);
  const [withRefs, setWithRefs] = useState(false);

  // Нетронутая строка — по справочнику (галочка `default_on`, размер по умолчанию).
  const picks = useMemo(
    () => Object.fromEntries(models.map((m) => [m.code, touched[m.code] ?? initialPick(m)])),
    [models, touched],
  );
  const body = useMemo(() => runBody(models, picks, seamless, withRefs), [models, picks, seamless, withRefs]);
  const estimate = useEstimate(pid, body);
  // Запуск прошёл — выбор снимается в колбэке мутации: тот же набор второй раз — только заново.
  const run = useRunClips(pid, () =>
    setTouched((prev) =>
      Object.fromEntries(models.map((m) => [m.code, { ...(prev[m.code] ?? initialPick(m)), on: false }])),
    ),
  );

  const est = estimate.fresh && !estimate.isFetching && estimate.data ? estimate.data : null;
  const totals = useMemo(() => modelTotals(est?.items ?? []), [est]);
  const clips = est?.items.length ?? 0;
  // Новых платных клипов: переиспользованные (клип запасной модели уже есть) денег не просят.
  const fresh = est?.items.filter((i) => !i.reused).length ?? 0;
  const ready = canRun({
    fresh: estimate.fresh && est !== null,
    fetching: estimate.isFetching,
    failed: estimate.isError,
    blockers: est?.blockers.length ?? 0,
    clips,
    busy: run.busy,
  });

  const patch = (m: VideoModel, change: Partial<RunPick>) =>
    setTouched((prev) => ({ ...prev, [m.code]: { ...(prev[m.code] ?? initialPick(m)), ...change } }));

  // Таблица моделей — в окне (задача Оли №335: «не удобно что экран начинается с громоздкой
  // таблицы»). Умолчание прежнее — отмечен Kling 3.0 Pro (`default_on`), поэтому запускают сразу,
  // не открывая окно; открывают только чтобы сменить модель или размер.
  const [picker, setPicker] = useState(false);
  const chosen = useMemo(() => pickedSummary(models, picks, label), [models, picks, label]);

  return (
    <Panel title="Прогнать на моделях" annot="запуск по моделям">
      {options.isPending ? (
        <Hint>загружаю модели…</Hint>
      ) : options.isError ? (
        <ErrorLine text={errorText(options.error)} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
            <span className="text-fg-faint">модели:</span>
            {chosen.length === 0 ? (
              <span className="text-warn">ни одной не выбрано</span>
            ) : (
              chosen.map((line) => (
                <span key={line} className="rounded-full bg-neutral-bg px-2 py-0.5 text-fg">
                  {line}
                </span>
              ))
            )}
            <Btn tone="ghost" onClick={() => setPicker(true)} title="выбрать модели и размеры">
              Выбрать модели
            </Btn>
          </div>

          <Dialog open={picker} title="Модели для клипов" onClose={() => setPicker(false)} wide>
            <div className="grid gap-2 p-3">
              <Hint>модели по очереди уровней: дешёвая → ценная → средняя → дорогая</Hint>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[12.5px]">
                  <thead>
                    <tr className="border-b border-border text-left text-[11.5px] text-fg-faint">
                      <th className="w-7 py-1.5" />
                      <th className="py-1.5 pr-3 font-normal">модель</th>
                      <th className="py-1.5 pr-3 font-normal">уровень</th>
                      <th className="py-1.5 pr-3 font-normal" title={FACES_NOTE}>
                        лица*
                      </th>
                      <th className="py-1.5 pr-3 font-normal">размер</th>
                      <th className="py-1.5 text-right font-normal">цена за ролик</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ model: m, nested }) => (
                      <ModelRow
                        key={m.code}
                        model={m}
                        nested={nested}
                        pick={picks[m.code]!}
                        route={faceRoute(m, label)}
                        total={totals.get(m.code) ?? null}
                        estimated={est !== null}
                        onPatch={(change) => patch(m, change)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <Hint className="mt-1.5">* {FACES_NOTE}</Hint>
              <div className="mt-2 flex justify-end">
                <Btn tone="primary" onClick={() => setPicker(false)}>
                  Готово
                </Btn>
              </div>
            </div>
          </Dialog>

          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-[12.5px]">
            <label className="inline-flex items-center gap-1.5">
              <input type="checkbox" checked={seamless} onChange={(e) => setSeamless(e.target.checked)} />
              бесшовно (last_frame)
            </label>
            <label className="inline-flex items-center gap-1.5" title="фото платья уходят провайдеру как есть — с лицом">
              <input type="checkbox" checked={withRefs} onChange={(e) => setWithRefs(e.target.checked)} />
              референсы платья <span className="text-warn">(фото с лицом!)</span>
            </label>
          </div>

          <div className="mt-3 grid gap-2 border-t border-border pt-3">
            <CapField pid={pid} costCap={costCap} />
            {body === null ? (
              <Hint>Выберите модели — оценка посчитается сама.</Hint>
            ) : !estimate.fresh || estimate.isFetching ? (
              <Hint>считаю оценку…</Hint>
            ) : estimate.isError ? (
              <ErrorLine text={errorText(estimate.error)} />
            ) : est ? (
              <>
                {est.blockers.length > 0 ? (
                  <ul className="grid gap-1 rounded-[var(--radius-ui)] border border-err/40 bg-err-bg px-2.5 py-1.5 text-[12px] text-err">
                    {est.blockers.map((b) => {
                      const sid = blockerShot(b, timeline);
                      return (
                        <li key={b}>
                          {sid !== null ? (
                            <Link to={`/project/${pid}/shot/${sid}`} className="underline hover:no-underline">
                              {b}
                            </Link>
                          ) : (
                            b
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {uniqueWarnings(est.items).map((w) => (
                  <Hint key={w}>⚠ {w}</Hint>
                ))}
                <RouteList lines={routeLines(est.items, label, orderOf)} />
                <div className="text-[12.5px] text-fg">
                  Итого {clips} {plural(clips, "клип", "клипа", "клипов")}
                  {fresh < clips ? ` (новых ${fresh}, уже есть ${clips - fresh})` : ""} ≈{" "}
                  <span className="tnum">{usd(est.total, 2)}</span>
                  {" · "}потолок проекта:{" "}
                  {est.cap_left === null ? "не задан" : <>осталось <span className="tnum">{usd(est.cap_left, 2)}</span></>}
                  {" · "}баланс {est.balance ? <span className="tnum">{usd(est.balance.left, 2)}</span> : "неизвестен"}
                </div>
                {est.balance_warning ? (
                  <div className="rounded-[var(--radius-ui)] border border-warn/40 bg-warn-bg px-2.5 py-1.5 text-[12px] text-warn">
                    {est.balance_warning}
                  </div>
                ) : null}
              </>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <Btn
                tone="primary"
                disabled={!ready}
                onClick={() => body && ready && run.run(body)}
                title="ПЛАТНО: клипы уходят в очередь, оплачивает воркер"
              >
                <Play size={13} />
                {run.busy
                  ? "ставлю в очередь…"
                  : est && fresh > 0
                    ? `Запустить ${fresh} ${plural(fresh, "клип", "клипа", "клипов")} за ≈ ${usd(est.total, 2)}`
                    : est && clips > 0
                      ? "Запустить (клипы уже есть — без оплаты)"
                      : "Запустить"}
              </Btn>
              {run.data ? (
                <Hint>
                  В очереди: {run.data.items.filter((i) => !i.existing).length}
                  {run.data.items.some((i) => i.existing)
                    ? ` (уже были: ${run.data.items.filter((i) => i.existing).length})`
                    : ""}{" "}
                  ≈ {usd(run.data.total, 2)} — статус в «Клипах» ниже.
                </Hint>
              ) : null}
            </div>
            {run.data ? <RouteList lines={routeLines(run.data.items, label, orderOf)} /> : null}
            {run.data?.balance_warning ? (
              <div className="rounded-[var(--radius-ui)] border border-warn/40 bg-warn-bg px-2.5 py-1.5 text-[12px] text-warn">
                {run.data.balance_warning}
              </div>
            ) : null}
            <ErrorLine text={run.error} />
          </div>
        </>
      )}
    </Panel>
  );
}

/** Потолок трат проекта (§11): клипы и стиллы вместе; превышение — блокер оценки и 400 `CAP` запуска. */
function CapField({ pid, costCap }: { pid: number; costCap: number | null }) {
  const settings = useVideoSettings(pid);
  const saved = costCap === null ? "" : costCap.toFixed(2);
  const [text, setText] = useState(saved);
  useEffect(() => setText(saved), [saved]);
  const value = parseCap(text);
  const dirty = value !== undefined && value !== costCap;
  return (
    <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
      <label className="inline-flex items-center gap-1.5">
        потолок проекта, $
        <input
          className={clsx(inputClass, "w-24 tnum", value === undefined && "border-err")}
          inputMode="decimal"
          placeholder="без потолка"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <Btn
        disabled={!dirty || settings.isPending}
        onClick={() => value !== undefined && settings.mutate({ cost_cap: value })}
        title="пусто — без потолка"
      >
        {settings.isPending ? "сохраняю…" : "Сохранить"}
      </Btn>
      {value === undefined ? <Hint>нужно число ≥ 0 или пусто</Hint> : null}
      <ErrorLine text={settings.isError ? errorText(settings.error) : ""} />
    </div>
  );
}

function ModelRow({
  model: m,
  nested,
  pick,
  route,
  total,
  estimated,
  onPatch,
}: {
  model: VideoModel;
  /** Запасная модель уровня — серой вложенной строкой под родителем. */
  nested: boolean;
  pick: RunPick;
  /** «кадры с лицом → Kling 3.0 Pro» у модели, отклоняющей лица. */
  route: string | null;
  total: { count: number; cost: number; routed: number; reused: number } | null;
  estimated: boolean;
  onPatch: (change: Partial<RunPick>) => void;
}) {
  const blocked = modelBlock(m);
  const chip = FACES_CHIP[m.faces];
  return (
    <tr
      className={clsx(
        "border-b border-border align-top",
        (blocked || nested) && "text-fg-faint",
        nested && "bg-bg/40",
      )}
    >
      <td className="py-1.5">
        <input
          type="checkbox"
          aria-label={m.label}
          disabled={blocked !== null}
          checked={pick.on && blocked === null}
          onChange={(e) => onPatch({ on: e.target.checked })}
        />
      </td>
      <td className={clsx("py-1.5 pr-3", nested && "pl-4")}>
        <div className={clsx("font-medium", nested ? "text-fg-muted" : "text-fg")} title={m.code}>
          {nested ? "↳ " : ""}
          {m.label}
        </div>
        {nested ? <div className="text-[11px]">запасная на уровне</div> : null}
        {route ? <div className="text-[11px] text-warn">{route}</div> : null}
        {blocked ? <div className="text-[11px] text-err">{blocked}</div> : null}
      </td>
      <td className="py-1.5 pr-3 text-[12px]">
        {m.tier_label ?? <span className="text-fg-faint">без уровня</span>}
      </td>
      <td className="py-1.5 pr-3">
        <span
          title={chip.title}
          className={clsx("inline-flex h-5 min-w-7 items-center justify-center rounded-full px-1.5 text-[11.5px]", FACES_TONE[m.faces])}
        >
          {chip.text}
        </span>
      </td>
      <td className="py-1.5 pr-3">
        <select
          aria-label={`размер ${m.label}`}
          className={selectClass}
          disabled={blocked !== null}
          value={pick.size}
          onChange={(e) => onPatch({ size: e.target.value })}
        >
          {sizeChoices(m).map((s) => (
            <option key={s} value={s}>
              {sizeLabel(m.send, s)}
            </option>
          ))}
        </select>
      </td>
      <td className="py-1.5 text-right whitespace-nowrap">
        {pick.on && total ? (
          <span className="text-fg">
            ≈ <span className="tnum">{usd(total.cost, 2)}</span>
            <Hint>
              {total.count} {plural(total.count, "клип", "клипа", "клипов")}
              {total.routed ? ` · ${total.routed} на запасной` : ""}
              {total.reused ? ` · ${total.reused} уже есть` : ""}
            </Hint>
          </span>
        ) : pick.on && estimated ? (
          <Hint>нет кадров с длительностью</Hint>
        ) : m.price_15s !== null ? (
          <span className="text-fg-muted">
            ≈ <span className="tnum">{usd(m.price_15s, 2)}</span>
            <Hint>за 15 с{m.price_basis === "table" ? " · по прайсу" : m.price_basis === "facts" ? " · по фактам" : ""}</Hint>
          </span>
        ) : (
          <Hint>—</Hint>
        )}
      </td>
    </tr>
  );
}

/** Разбор пунктов, ушедших на запасную модель или переиспользующих готовый клип. */
function RouteList({ lines }: { lines: string[] }) {
  if (lines.length === 0) return null;
  return (
    <ul className="grid gap-0.5 rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 py-1.5 text-[12px] text-fg-muted">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}
